import { db, storage } from './firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, getDoc, writeBatch } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

// License validation utility functions
export const getLicenseStatus = async (userId) => {
  try {
    // Get user's company data
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return { valid: false, reason: 'user_not_found' };
    }
    
    const companyId = userDoc.data().companyId;
    if (!companyId) {
      return { valid: false, reason: 'no_company' };
    }
    
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (!companyDoc.exists()) {
      return { valid: false, reason: 'company_not_found' };
    }
    
    const companyData = companyDoc.data();
    const userEmail = userDoc.data().email;
    const now = new Date();
    
    // Find user's license
    const userLicense = companyData.licenses?.find(license => 
      license.email === userEmail || 
      (license.admin && userDoc.data().uid === companyData.adminUserId)
    );
    
    if (!userLicense) {
      return { valid: false, reason: 'no_license' };
    }
    
    // Check license status
    if (userLicense.status === 'suspended') {
      return { 
        valid: false, 
        reason: 'suspended',
        suspendedAt: userLicense.suspendedAt,
        suspensionReason: userLicense.suspensionReason
      };
    }
    
    // Check if in grace period
    if (userLicense.gracePeriodEnd) {
      const gracePeriodEnd = new Date(userLicense.gracePeriodEnd);
      if (gracePeriodEnd > now) {
        return {
          valid: true,
          inGracePeriod: true,
          gracePeriodEnd: gracePeriodEnd,
          status: userLicense.status,
          paymentFailureCount: userLicense.paymentFailureCount || 0,
          readOnlyMode: false
        };
      } else {
        // Grace period expired - should be suspended
        return {
          valid: false,
          reason: 'grace_period_expired',
          gracePeriodEnd: gracePeriodEnd
        };
      }
    }
    
    // Check expiration date
    if (userLicense.expirationDate) {
      const expirationDate = new Date(userLicense.expirationDate);
      if (expirationDate <= now) {
        return { 
          valid: false, 
          reason: 'expired',
          expirationDate: expirationDate
        };
      }
    }
    
    // License is valid
    return {
      valid: true,
      inGracePeriod: false,
      status: userLicense.status,
      expirationDate: userLicense.expirationDate,
      readOnlyMode: false
    };
    
  } catch (err) {
    console.error('API: Error checking license status:', err);
    return { valid: false, reason: 'error', error: err.message };
  }
};

// Wrapper function to check license before API operations
const checkLicenseForOperation = async (userId, operationType = 'read') => {
  const licenseStatus = await getLicenseStatus(userId);
  
  if (!licenseStatus.valid) {
    throw new Error(`Access denied: ${licenseStatus.reason}`);
  }
  
  // For write operations, check if in grace period
  if (operationType === 'write' && licenseStatus.inGracePeriod) {
    // Allow read operations during grace period, but warn about write operations
    console.warn('API: User is in grace period, write operations allowed but payment issue exists');
  }
  
  return licenseStatus;
};

export const createBid = async (userId, bidData) => {
  try {
    // Check license status before creating bid
    await checkLicenseForOperation(userId, 'write');
    
    // Fetch user's companyId
    const userDoc = await getDoc(doc(db, 'users', userId));
    const companyId = userDoc.exists() ? userDoc.data().companyId : null;
    console.log('API: Creating new bid with userId:', userId, 'companyId:', companyId);
    console.log('API: Bid data to be stored:', {
      ...bidData,
      userId,
      companyId,
      createdAt: new Date().toISOString()
    });
    
    // Validate required fields
    if (!userId) {
      throw new Error('User ID is required to create a bid');
    }
    
    // Ensure the bid data has the correct structure
    const bidToCreate = {
      ...bidData,
      userId,
      companyId,
      createdAt: new Date().toISOString(),
    };
    
    // Create the bid in Firestore
    console.log('API: Executing addDoc to Firestore collection "bids"');
    const bidRef = await addDoc(collection(db, 'bids'), bidToCreate);
    
    console.log('API: Bid created successfully with ID:', bidRef.id);
    return { id: bidRef.id, ...bidToCreate };
  } catch (err) {
    console.error('API: Error creating bid:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    
    // Rethrow with additional information
    throw err;
  }
};

export const getBids = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const companyId = userDoc.exists() ? userDoc.data().companyId : null;
    const queries = [
      query(collection(db, 'bids'), where('userId', '==', userId)),
    ];
    if (companyId) {
      queries.push(query(collection(db, 'bids'), where('companyId', '==', companyId)));
    }
    let bids = [];
    for (const q of queries) {
      const snapshot = await getDocs(q);
      snapshot.forEach(docSnap => {
        const data = { id: docSnap.id, ...docSnap.data() };
        // Avoid duplicates
        if (!bids.some(b => b.id === data.id)) {
          bids.push(data);
        }
      });
    }
    return bids;
  } catch (err) {
    console.error('API: Error fetching bids:', err);
    throw err;
  }
};

export const updateBid = async (bidId, updatedData) => {
  try {
    console.log('API: Updating bid with ID:', bidId);
    const bidRef = doc(db, 'bids', bidId);
    await updateDoc(bidRef, updatedData);
    console.log('API: Bid updated successfully:', bidId);
  } catch (err) {
    console.error('API: Error updating bid:', err);
    throw err;
  }
};

export const deleteBid = async (bidId) => {
  try {
    console.log('API: Deleting bid with ID:', bidId);
    const bidRef = doc(db, 'bids', bidId);
    const subbidsRef = collection(db, 'bids', bidId, 'subbids');

    // 1. Get the bid data to check for attachments
    const bidDoc = await getDoc(bidRef);
    if (bidDoc.exists()) {
      const bidData = bidDoc.data();
      
      // Delete any attached files from Firebase Storage
      if (bidData.attachments && bidData.attachments.storagePath) {
        try {
          const fileRef = ref(storage, bidData.attachments.storagePath);
          await deleteObject(fileRef);
          console.log('API: Deleted attached file from storage:', bidData.attachments.storagePath);
        } catch (storageError) {
          console.error('API: Error deleting attached file from storage:', storageError);
          // Continue with bid deletion even if file deletion fails
        }
      }
    }

    // 2. Get all sub-bids
    const subbidsSnapshot = await getDocs(subbidsRef);
    
    // 3. Delete all sub-bids in a batch
    const batch = writeBatch(db);
    subbidsSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();
    console.log(`API: Deleted ${subbidsSnapshot.size} sub-bids for bid ${bidId}`);

    // 4. Delete the parent bid
    await deleteDoc(bidRef);
    console.log('API: Bid deleted successfully:', bidId);
  } catch (err) {
    console.error('API: Error deleting bid:', err);
    throw err;
  }
};

// Sub-bid operations

export const createSubBid = async (bidId, subBidData) => {
  try {
    // Fetch parent bid to get companyId
    const bidDoc = await getDoc(doc(db, 'bids', bidId));
    const companyId = bidDoc.exists() ? bidDoc.data().companyId : null;
    console.log('API: Creating new sub-bid for bid ID:', bidId, 'companyId:', companyId);
    console.log('API: Sub-bid data to be stored:', {
      ...subBidData,
      companyId,
      createdAt: new Date().toISOString()
    });
    if (!bidId) {
      throw new Error('Bid ID is required to create a sub-bid');
    }
    const subBidToCreate = {
      ...subBidData,
      companyId,
      createdAt: new Date().toISOString(),
    };
    const subBidRef = await addDoc(collection(db, 'bids', bidId, 'subbids'), subBidToCreate);
    console.log('API: Sub-bid created successfully with ID:', subBidRef.id);
    return { id: subBidRef.id, ...subBidToCreate };
  } catch (err) {
    console.error('API: Error creating sub-bid:', err);
    throw err;
  }
};

export const getSubBids = async (bidId, userId) => {
  try {
    // Fetch parent bid to get companyId
    const bidDoc = await getDoc(doc(db, 'bids', bidId));
    const companyId = bidDoc.exists() ? bidDoc.data().companyId : null;
    const queries = [
      collection(db, 'bids', bidId, 'subbids'),
    ];
    let subBids = [];
    for (const col of queries) {
      const snapshot = await getDocs(col);
      snapshot.forEach(docSnap => {
        const data = { id: docSnap.id, ...docSnap.data() };
        // Filter by userId or companyId
        if ((userId && data.userId === userId) || (companyId && data.companyId === companyId)) {
          if (!subBids.some(s => s.id === data.id)) {
            subBids.push(data);
          }
        }
      });
    }
    return subBids;
  } catch (err) {
    console.error('API: Error fetching sub-bids:', err);
    throw err;
  }
};

export const updateSubBid = async (bidId, subBidId, updatedData) => {
  try {
    console.log('API: Updating sub-bid with ID:', subBidId, 'for bid ID:', bidId);
    
    if (!bidId || !subBidId) {
      throw new Error('Bid ID and sub-bid ID are required to update a sub-bid');
    }
    
    const subBidRef = doc(db, 'bids', bidId, 'subbids', subBidId);
    await updateDoc(subBidRef, updatedData);
    
    console.log('API: Sub-bid updated successfully:', subBidId);
  } catch (err) {
    console.error('API: Error updating sub-bid:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const deleteSubBid = async (bidId, subBidId) => {
  try {
    console.log('API: Deleting sub-bid with ID:', subBidId, 'for bid ID:', bidId);
    
    if (!bidId || !subBidId) {
      throw new Error('Bid ID and sub-bid ID are required to delete a sub-bid');
    }
    
    const subBidRef = doc(db, 'bids', bidId, 'subbids', subBidId);
    await deleteDoc(subBidRef);
    
    console.log('API: Sub-bid deleted successfully:', subBidId);
  } catch (err) {
    console.error('API: Error deleting sub-bid:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

// Contact operations

export const createContact = async (userId, contactData) => {
  try {
    // Fetch user's companyId
    const userDoc = await getDoc(doc(db, 'users', userId));
    const companyId = userDoc.exists() ? userDoc.data().companyId : null;
    console.log('API: Creating new contact with userId:', userId, 'companyId:', companyId);
    console.log('API: Contact data to be stored:', {
      ...contactData,
      userId,
      companyId,
      createdAt: new Date().toISOString()
    });
    
    // Validate required fields
    if (!userId) {
      throw new Error('User ID is required to create a contact');
    }
    
    // Ensure the contact data has the correct structure
    const contactToCreate = {
      ...contactData,
      userId,
      companyId,
      createdAt: new Date().toISOString(),
    };
    
    // Create the contact in Firestore
    console.log('API: Executing addDoc to Firestore collection "contacts"');
    const contactRef = await addDoc(collection(db, 'contacts'), contactToCreate);
    
    console.log('API: Contact created successfully with ID:', contactRef.id);
    return { id: contactRef.id, ...contactToCreate };
  } catch (err) {
    console.error('API: Error creating contact:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    
    // Rethrow with additional information
    throw err;
  }
};

export const getContacts = async (userId, type) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const companyId = userDoc.exists() ? userDoc.data().companyId : null;
    const queries = [
      query(collection(db, 'contacts'), where('userId', '==', userId), where('type', '==', type)),
    ];
    if (companyId) {
      queries.push(query(collection(db, 'contacts'), where('companyId', '==', companyId), where('type', '==', type)));
    }
    let contacts = [];
    for (const q of queries) {
      const snapshot = await getDocs(q);
      snapshot.forEach(docSnap => {
        const data = { id: docSnap.id, ...docSnap.data() };
        if (!contacts.some(c => c.id === data.id)) {
          contacts.push(data);
        }
      });
    }
    return contacts;
  } catch (err) {
    console.error('API: Error fetching contacts:', err);
    throw err;
  }
};

export const updateContact = async (contactId, updatedData) => {
  try {
    console.log('API: Updating contact with ID:', contactId);
    
    if (!contactId) {
      throw new Error('Contact ID is required to update a contact');
    }
    
    const contactRef = doc(db, 'contacts', contactId);
    await updateDoc(contactRef, updatedData);
    
    console.log('API: Contact updated successfully:', contactId);
  } catch (err) {
    console.error('API: Error updating contact:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const deleteContact = async (contactId) => {
  try {
    console.log('API: Deleting contact with ID:', contactId);
    
    if (!contactId) {
      throw new Error('Contact ID is required to delete a contact');
    }
    
    const contactRef = doc(db, 'contacts', contactId);
    await deleteDoc(contactRef);
    
    console.log('API: Contact deleted successfully:', contactId);
  } catch (err) {
    console.error('API: Error deleting contact:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

// Analytics operations

export const getBidAnalytics = async (userId, dateFrom = null, dateTo = null) => {
  try {
    console.log('API: Generating bid analytics for user ID:', userId);
    if (dateFrom || dateTo) {
      console.log('API: Date range filter:', dateFrom, 'to', dateTo);
    }

    // First get all the bids for this user
    let bids = await getBids(userId);

    // Filter by date range if provided
    if (dateFrom || dateTo) {
      bids = bids.filter(bid => {
        const bidDate = bid.DateIssued ? new Date(bid.DateIssued) : null;
        if (!bidDate) return true; // Include bids without dates

        if (dateFrom && dateTo) {
          return bidDate >= new Date(dateFrom) && bidDate <= new Date(dateTo);
        } else if (dateFrom) {
          return bidDate >= new Date(dateFrom);
        } else if (dateTo) {
          return bidDate <= new Date(dateTo);
        }
        return true;
      });
      console.log(`API: Bids after date filtering: ${bids.length}`);
    }

    console.log(`API: Total bids before deduplication: ${bids.length}`);
    
    // Deduplication: Group bids by project, keep awarded if exists, else latest
    // User confirmed: "Awarded if exists, else latest" + parent BidAmount excludes sub-bids
    const uniqueProjects = {};
    // Sort DESC (newest first) to get latest bids first
    const sortedBids = [...bids].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    for (const bid of sortedBids) {
      // Standalone bids get unique keys, projects grouped by JobName
      const key = bid.JobName || `standalone_${bid.id}`;
      const isAwarded = bid.BidStatus === 'Awarded';

      // Keep first occurrence (latest by createdAt), OR replace with awarded bid
      if (!uniqueProjects[key]) {
        uniqueProjects[key] = bid;
      } else {
        // If current bid is awarded and stored bid is not, replace it
        if (isAwarded && uniqueProjects[key].BidStatus !== 'Awarded') {
          uniqueProjects[key] = bid;
        }
      }
    }

    const uniqueBids = Object.values(uniqueProjects);

    // Calculate totals from deduplicated bids with sub-bid integration
    let totalProposed = 0;
    let totalAwarded = 0;
    let totalSubBidProposed = 0;  // NEW: Track sub-bid totals
    let totalSubBidAwarded = 0;   // NEW: Track awarded sub-bids

    for (const bid of uniqueBids) {
      totalProposed += Number(bid.BidAmount) || 0;

      if (bid.BidStatus === 'Awarded') {
        totalAwarded += Number(bid.AwardedAmount || bid.BidAmount) || 0;

        // Fetch and sum sub-bids for awarded project bids (not standalone)
        if (bid.type === 'project' || bid.type === 'incoming') {
          try {
            const subBids = await getSubBids(bid.id, userId);
            const subProposed = subBids.reduce((sum, sub) => sum + (Number(sub.SubBidAmount) || 0), 0);
            const subAwarded = subBids.reduce((sum, sub) => sum + (Number(sub.AwardedAmount) || 0), 0);

            totalSubBidProposed += subProposed;
            totalSubBidAwarded += subAwarded;
          } catch (err) {
            console.error('API: Error fetching sub-bids for bid', bid.id, ':', err);
          }
        }
      }
    }

    // Calculate buyout profit (GC bid amount - sub-bid costs)
    const buyoutProfit = totalAwarded - totalSubBidAwarded;
    const profitMargin = totalAwarded > 0 ? ((buyoutProfit / totalAwarded) * 100) : 0;

    // Calculate metrics from deduplicated bids
    const totalBids = uniqueBids.length;
    const projectBids = uniqueBids.filter(bid => bid.type === 'project' || bid.type === 'incoming').length;
    const standaloneBids = uniqueBids.filter(bid => bid.type === 'standalone').length;

    // Status counts and values (from deduplicated bids)
    const statusCounts = {
      Open: uniqueBids.filter(b => b.BidStatus === 'Open').length,
      Submitted: uniqueBids.filter(b => b.BidStatus === 'Submitted').length,
      Awarded: uniqueBids.filter(b => b.BidStatus === 'Awarded').length,
      Lost: uniqueBids.filter(b => b.BidStatus === 'Lost').length,
      'On Hold': uniqueBids.filter(b => b.BidStatus === 'On Hold').length
    };

    const statusValues = {
      Open: uniqueBids.filter(b => b.BidStatus === 'Open').reduce((sum, b) => sum + (Number(b.BidAmount) || 0), 0),
      Submitted: uniqueBids.filter(b => b.BidStatus === 'Submitted').reduce((sum, b) => sum + (Number(b.BidAmount) || 0), 0),
      Awarded: uniqueBids.filter(b => b.BidStatus === 'Awarded').reduce((sum, b) => sum + (Number(b.AwardedAmount || b.BidAmount) || 0), 0),
      Lost: uniqueBids.filter(b => b.BidStatus === 'Lost').reduce((sum, b) => sum + (Number(b.BidAmount) || 0), 0),
      'On Hold': uniqueBids.filter(b => b.BidStatus === 'On Hold').reduce((sum, b) => sum + (Number(b.BidAmount) || 0), 0)
    };

    // Calculate wins/losses
    const wins = uniqueBids.filter(bid => bid.BidStatus === 'Awarded').length;
    const losses = uniqueBids.filter(bid => bid.BidStatus === 'Lost').length;
    const winRate = totalBids > 0 ? (wins / totalBids) * 100 : 0;

    // NEW: Calculate pipeline value (active bids not yet won or lost)
    const pipelineValue = statusValues.Open + statusValues.Submitted;

    console.log(`API: Total bids after deduplication: ${totalBids}`);
    console.log(`API: Total Proposed: $${totalProposed.toFixed(2)}, Total Awarded: $${totalAwarded.toFixed(2)}`);
    console.log(`API: Buyout Profit: $${buyoutProfit.toFixed(2)}, Margin: ${profitMargin.toFixed(2)}%`);

    const analytics = {
      totalBids,
      projectBids,
      standaloneBids,
      wins,
      losses,
      winRate,
      totalProposed,
      totalAwarded,
      totalSubBidProposed,      // NEW
      totalSubBidAwarded,       // NEW
      buyoutProfit,             // NEW
      profitMargin,             // NEW
      pipelineValue,            // NEW
      statusCounts,
      statusValues
    };
    
    console.log('API: Generated bid analytics:', analytics);
    return analytics;
  } catch (err) {
    console.error('API: Error fetching bid analytics:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const getClientAnalytics = async (userId, dateFrom = null, dateTo = null) => {
  try {
    console.log('API: Generating client analytics for user ID:', userId);

    // First get all the bids for this user
    let bids = await getBids(userId);

    // Filter by date range if provided
    if (dateFrom || dateTo) {
      bids = bids.filter(bid => {
        const bidDate = bid.DateIssued ? new Date(bid.DateIssued) : null;
        if (!bidDate) return true; // Include bids without dates

        if (dateFrom && dateTo) {
          return bidDate >= new Date(dateFrom) && bidDate <= new Date(dateTo);
        } else if (dateFrom) {
          return bidDate >= new Date(dateFrom);
        } else if (dateTo) {
          return bidDate <= new Date(dateTo);
        }
        return true;
      });
    }

    // Filter to all bids with a client name (project and standalone)
    const clientBids = bids.filter(bid => bid.ClientName && bid.ClientName.trim() !== '');

    console.log(`API: Client Analytics - Total bids before deduplication: ${clientBids.length}`);

    // CRITICAL FIX: Deduplicate bids by client → project BEFORE summing amounts
    // This prevents counting multiple revisions of the same project
    const clientProjectMap = {};

    for (const bid of clientBids) {
      const clientName = bid.ClientName || 'Unknown';
      const projectKey = bid.JobName || `standalone_${bid.id}`;

      if (!clientProjectMap[clientName]) {
        clientProjectMap[clientName] = {};
      }

      // Keep awarded bid if exists, else keep latest (most recent createdAt)
      if (!clientProjectMap[clientName][projectKey]) {
        clientProjectMap[clientName][projectKey] = bid;
      } else {
        const existing = clientProjectMap[clientName][projectKey];
        // Replace if current is awarded and existing is not
        if (bid.BidStatus === 'Awarded' && existing.BidStatus !== 'Awarded') {
          clientProjectMap[clientName][projectKey] = bid;
        } else if (bid.BidStatus === existing.BidStatus && (bid.createdAt || 0) > (existing.createdAt || 0)) {
          // If same status, keep more recent
          clientProjectMap[clientName][projectKey] = bid;
        }
      }
    }

    // Now calculate analytics from deduplicated bids
    const clientAnalytics = {};

    for (const [clientName, projects] of Object.entries(clientProjectMap)) {
      const dedupedClientBids = Object.values(projects);

      clientAnalytics[clientName] = {
        totalBids: dedupedClientBids.length,
        wins: 0,
        losses: 0,
        totalBid: 0,
        totalProposed: 0,
        totalAwarded: 0,
        subTotalAwarded: 0,
        buyoutProfit: 0,
        statusCounts: { Open: 0, Submitted: 0, Awarded: 0, Lost: 0, 'On Hold': 0 }
      };

      for (const bid of dedupedClientBids) {
        // Status counts
        if (bid.BidStatus) {
          clientAnalytics[clientName].statusCounts[bid.BidStatus] += 1;
        } else {
          clientAnalytics[clientName].statusCounts['Open'] += 1;
        }

        // Wins/losses
        if (bid.BidStatus === 'Awarded') {
          clientAnalytics[clientName].wins += 1;
        } else if (bid.BidStatus === 'Lost') {
          clientAnalytics[clientName].losses += 1;
        }

        // Financial totals (now deduplicated - no more double-counting!)
        clientAnalytics[clientName].totalBid += Number(bid.BidAmount) || 0;
        clientAnalytics[clientName].totalProposed += Number(bid.BidAmount) || 0;

        // For awarded bids, add to total awarded and fetch sub-bids
        if (bid.BidStatus === 'Awarded') {
          clientAnalytics[clientName].totalAwarded += Number(bid.AwardedAmount || bid.BidAmount) || 0;

          // Fetch sub-bids for awarded project bids (not standalone)
          if (bid.type === 'project' || bid.type === 'incoming') {
            try {
              const subBids = await getSubBids(bid.id, userId);
              const subTotal = subBids.reduce((sum, sub) => sum + (Number(sub.AwardedAmount) || 0), 0);
              clientAnalytics[clientName].subTotalAwarded += subTotal;
            } catch (err) {
              console.error('API: Error fetching sub-bids for client bid:', err);
            }
          }
        }
      }

      // Calculate win rate and buyout profit for this client
      clientAnalytics[clientName].winRate =
        clientAnalytics[clientName].totalBids > 0
          ? (clientAnalytics[clientName].wins / clientAnalytics[clientName].totalBids) * 100
          : 0;

      clientAnalytics[clientName].buyoutProfit =
        clientAnalytics[clientName].totalAwarded - clientAnalytics[clientName].subTotalAwarded;
    }

    console.log(`API: Client Analytics - Clients processed: ${Object.keys(clientAnalytics).length}`);
    
    console.log('API: Generated client analytics:', clientAnalytics);
    return clientAnalytics;
  } catch (err) {
    console.error('API: Error fetching client analytics:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const getProjectAnalytics = async (userId, dateFrom = null, dateTo = null) => {
  try {
    console.log('API: Generating project analytics for user ID:', userId);

    // First get all the bids for this user
    let bids = await getBids(userId);

    // Filter by date range if provided
    if (dateFrom || dateTo) {
      bids = bids.filter(bid => {
        const bidDate = bid.DateIssued ? new Date(bid.DateIssued) : null;
        if (!bidDate) return true; // Include bids without dates

        if (dateFrom && dateTo) {
          return bidDate >= new Date(dateFrom) && bidDate <= new Date(dateTo);
        } else if (dateFrom) {
          return bidDate >= new Date(dateFrom);
        } else if (dateTo) {
          return bidDate <= new Date(dateTo);
        }
        return true;
      });
    }

    // Filter to project bids only (those with JobName/project names)
    const projectBids = bids.filter(bid => bid.JobName && bid.JobName.trim() !== '');

    console.log(`API: Project Analytics - Total bids before deduplication: ${projectBids.length}`);

    // CRITICAL FIX: Deduplicate bids by project name BEFORE summing amounts
    // Keep awarded bid if exists, else keep latest (most recent createdAt)
    const projectBidMap = {};

    for (const bid of projectBids) {
      const projectName = bid.JobName || 'Unknown';

      // Keep awarded bid if exists, else keep latest
      if (!projectBidMap[projectName]) {
        projectBidMap[projectName] = bid;
      } else {
        const existing = projectBidMap[projectName];
        // Replace if current is awarded and existing is not
        if (bid.BidStatus === 'Awarded' && existing.BidStatus !== 'Awarded') {
          projectBidMap[projectName] = bid;
        } else if (bid.BidStatus === existing.BidStatus && (bid.createdAt || 0) > (existing.createdAt || 0)) {
          // If same status, keep more recent
          projectBidMap[projectName] = bid;
        }
      }
    }

    // Now calculate analytics from deduplicated bids
    const projectAnalytics = {};

    for (const [projectName, bid] of Object.entries(projectBidMap)) {
      const clientName = bid.ClientName || 'Unknown Client';

      projectAnalytics[projectName] = {
        clientName: clientName,
        totalBids: 1,  // Always 1 since we deduplicated
        wins: 0,
        losses: 0,
        totalBid: 0,
        totalProposed: 0,
        totalAwarded: 0,
        subTotalAwarded: 0,
        buyoutProfit: 0,
        statusCounts: { Open: 0, Submitted: 0, Awarded: 0, Lost: 0, 'On Hold': 0 }
      };

      // Status counts
      if (bid.BidStatus) {
        projectAnalytics[projectName].statusCounts[bid.BidStatus] = 1;
      } else {
        projectAnalytics[projectName].statusCounts['Open'] = 1;
      }

      // Wins/losses
      if (bid.BidStatus === 'Awarded') {
        projectAnalytics[projectName].wins = 1;
      } else if (bid.BidStatus === 'Lost') {
        projectAnalytics[projectName].losses = 1;
      }

      // Financial totals (now deduplicated)
      projectAnalytics[projectName].totalBid = Number(bid.BidAmount) || 0;
      projectAnalytics[projectName].totalProposed = Number(bid.BidAmount) || 0;

      // For awarded bids, fetch sub-bids
      if (bid.BidStatus === 'Awarded') {
        projectAnalytics[projectName].totalAwarded = Number(bid.AwardedAmount || bid.BidAmount) || 0;

        // Fetch sub-bids for awarded project bids
        if (bid.type === 'project' || bid.type === 'incoming') {
          try {
            const subBids = await getSubBids(bid.id, userId);
            const subTotal = subBids.reduce((sum, sub) => sum + (Number(sub.AwardedAmount) || 0), 0);
            projectAnalytics[projectName].subTotalAwarded = subTotal;
          } catch (err) {
            console.error('API: Error fetching sub-bids for project bid:', err);
          }
        }
      }

      // Calculate win rate and buyout profit for this project
      projectAnalytics[projectName].winRate =
        projectAnalytics[projectName].totalBids > 0
          ? (projectAnalytics[projectName].wins / projectAnalytics[projectName].totalBids) * 100
          : 0;

      projectAnalytics[projectName].buyoutProfit =
        projectAnalytics[projectName].totalAwarded - projectAnalytics[projectName].subTotalAwarded;
    }

    console.log(`API: Project Analytics - Projects processed: ${Object.keys(projectAnalytics).length}`);
    return projectAnalytics;
  } catch (err) {
    console.error('API: Error fetching project analytics:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const getSubcontractorAnalytics = async (userId, dateFrom = null, dateTo = null) => {
  try {
    console.log('API: Generating subcontractor analytics for user ID:', userId);

    // First get all the bids for this user
    let bids = await getBids(userId);

    // Filter by date range if provided
    if (dateFrom || dateTo) {
      bids = bids.filter(bid => {
        const bidDate = bid.DateIssued ? new Date(bid.DateIssued) : null;
        if (!bidDate) return true; // Include bids without dates

        if (dateFrom && dateTo) {
          return bidDate >= new Date(dateFrom) && bidDate <= new Date(dateTo);
        } else if (dateFrom) {
          return bidDate >= new Date(dateFrom);
        } else if (dateTo) {
          return bidDate <= new Date(dateTo);
        }
        return true;
      });
    }

    // Filter to only project bids
    const projectBids = bids.filter(bid => bid.type === 'project' || bid.type === 'incoming');
    
    // Process analytics by subcontractor
    const subcontractorAnalytics = {};
    const leaderboard = [];

    for (const bid of projectBids) {
      try {
        // Get sub-bids for this bid
        const subBids = await getSubBids(bid.id, userId);
        
        // Determine if the parent bid is awarded
        const isBidAwarded = bid.BidStatus === 'Awarded' || (bid.BidStatus === 'Closed' && bid.AwardedAmount > 0);
        
        for (const subBid of subBids) {
          const subName = subBid.SubcontractorName || 'Unknown';
          
          if (!subcontractorAnalytics[subName]) {
            subcontractorAnalytics[subName] = {
              totalSubBids: 0,
              wins: 0,
              losses: 0,
              totalBid: 0,         // Total of all their sub-bid amounts
              totalSubAwarded: 0   // Total of their awarded sub-bid amounts
            };
          }
          
          subcontractorAnalytics[subName].totalSubBids += 1;
          
          // Add their sub-bid amount to total bid
          subcontractorAnalytics[subName].totalBid += subBid.SubBidAmount || 0;
          
          if (bid.BidStatus === 'Awarded' || (bid.BidStatus === 'Closed' && subBid.SubBidAmount > 0)) {
            subcontractorAnalytics[subName].wins += 1;
          } else if (bid.BidStatus === 'Lost' || (bid.BidStatus === 'Closed' && subBid.SubBidAmount === 0)) {
            subcontractorAnalytics[subName].losses += 1;
          }
          
          // Only count awarded amount if the parent bid is awarded
          if (isBidAwarded) {
            subcontractorAnalytics[subName].totalSubAwarded += subBid.AwardedAmount || 0;
          }
        }
      } catch (subErr) {
        console.error('API: Error fetching sub-bids for bid', bid.id, ':', subErr);
        // Continue processing other bids
      }
    }

    // Calculate win rate and prepare leaderboard
    Object.keys(subcontractorAnalytics).forEach(subName => {
      const analytics = subcontractorAnalytics[subName];
      analytics.winRate = analytics.totalSubBids > 0 ? (analytics.wins / analytics.totalSubBids) * 100 : 0;
      
      leaderboard.push({
        name: subName,
        totalSubAwarded: analytics.totalSubAwarded,
        totalBid: analytics.totalBid,
        totalSubBids: analytics.totalSubBids  // Add total sub-bids to leaderboard
      });
    });

    // Sort leaderboard by total awarded amount (descending)
    leaderboard.sort((a, b) => b.totalSubAwarded - a.totalSubAwarded);
    
    console.log('API: Generated subcontractor analytics:', { subcontractorAnalytics, leaderboard });
    return { subcontractorAnalytics, leaderboard };
  } catch (err) {
    console.error('API: Error fetching subcontractor analytics:', err);
    console.error('API: Error code:', err.code);
    console.error('API: Error message:', err.message);
    throw err;
  }
};

export const createSubcontractor = async (userId, subcontractorData) => {
  try {
    // Fetch user's companyId
    const userDoc = await getDoc(doc(db, 'users', userId));
    const companyId = userDoc.exists() ? userDoc.data().companyId : null;
    console.log('API: Creating new subcontractor with userId:', userId, 'companyId:', companyId);
    const subcontractorToCreate = {
      ...subcontractorData,
      userId,
      companyId,
      createdAt: new Date().toISOString(),
    };
    const subRef = await addDoc(collection(db, 'subcontractors'), subcontractorToCreate);
    console.log('API: Subcontractor created successfully with ID:', subRef.id);
    return { id: subRef.id, ...subcontractorToCreate };
  } catch (err) {
    console.error('API: Error creating subcontractor:', err);
    throw err;
  }
}; 